import { Component, OnInit } from '@angular/core';
import { PasswordInput, PasswordValidatorType } from '@mpt-fo/common/kamona/components/kamona-input-field/kamona-input-field.component';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { MobileWebViewService } from '@mpt-fo/common/core/services/mobile-web-view.service';
import { ActivatedRoute, Router } from '@angular/router';
import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { AuthenticationService } from '@mpt-fo/common/core/services/authentication.service';
import { ModalOptionsConfig, ModalService } from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import {
	DEFAULT_DIALOG_CLASSES,
	DEFAULT_FAILED_OTP_MODAL_WIDTH,
	DEFAULT_MODAL_KAMONA_CLASSES,
	DEFAULT_MODAL_WIDTH,
	MONIEPOINT_DESIGN_TYPE,
} from '@mpt-fo/common/shared/constants';
import { BusinessService } from '@mpt-fo/common/core/services/business.service';
import { LocalStorageService } from '@mpt-fo/common/core/services/local-storage.service';
import { LivelinessErrorCode } from '@mpt-fo/common/core/services/liveliness.service';

@Component({
	selector: 'app-change-mobile-password',
	templateUrl: './change-mobile-password.component.html',
	styleUrls: ['./change-mobile-password.component.scss'],
})
export class ChangeMobilePasswordComponent implements OnInit {
	passwordInput: PasswordInput;
	submitting: boolean;
	runningModal: NzModalRef;
	isDisableButton: boolean;
	messageTitle: string;
	message: string;
	readonly SUCCESS_MESSAGE = 'Password change successful';
	readonly SUCCESS_TITLE = 'Request Successful';
	readonly ERROR_TITLE = 'Request Failed';
	token: any;
	validatingToken = true;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private authService: AuthenticationService,
		private mobileService: MobileWebViewService,
		private modalService: ModalService,
		private businessService: BusinessService,
		private localStorage: LocalStorageService
	) { }

	ngOnInit(): void {
		this.passwordInput = new PasswordInput(null, 'Password', true, PasswordValidatorType.CUSTOM);
		this.route.queryParams.subscribe((params) => {
			if (params?.validationKey) {
				this.saveCredentials(decodeURIComponent(params?.validationKey), params?.username);
				return;
			}
			this.initialize(decodeURIComponent(params?.token));
		});
	}

	initialize(token) {
		this.token = token;
		this.validateToken();
	}

	saveCredentials(validationKey, username) {
		this.mobileService.setChangePasswordCredential({
			username,
			validationKey,
			password: null,
		});
		this.validatingToken = false;
	}

	validateToken() {
		this.localStorage.deleteValue('access_token');
		this.businessService
			.validateActivationToken({ token: this.token })
			.then((res) => {
				this.router.navigate([`/mobile/recovery/password`], { queryParams: { username: res?.username } }).then((response) => {
					this.validatingToken = false;
				});
			})
			.catch((err) => {
				const error = StringHelper.getErrorMessageHttpErrorResponse(err);
				this.launchAlertModal({
					description: error,
					callbackName: 'Continue to login',
				});
				this.validatingToken = false;
			});
	}

	launchAlertModal(params) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.alertType = 'error';
		config.nzComponentParams.title = 'Token Validation Failed';
		config.className = DEFAULT_MODAL_KAMONA_CLASSES;
		config.nzWidth = DEFAULT_MODAL_WIDTH;
		config.nzComponentParams.canShowLoading = true;
		config.nzComponentParams.callback = () => {
			this.handleCloseModal();
		};
		config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
		config.nzComponentParams.callbackName = params?.callbackName || 'Close';
		config.nzComponentParams.message = params?.description;
		this.runningModal = this.modalService.alert(config);
	}

	buildParams() {
		const params = this.mobileService.getChangePasswordCredential();
		params.password = this.passwordInput.getInput();
		return params;
	}

	handleCloseModal() {
		this.router.navigateByUrl('/login').then((res) => {
			this.closeModal();
		});
	}

	restartProcess() {
		this.router.navigate([`mobile/recovery/password`]).then((response) => {
			this.closeModal();
		});
	}

	next() {
		this.submitting = true;
		const body = this.buildParams();
		this.authService
			.changePassword(body)
			.then((res) => {
				this.messageTitle = this.SUCCESS_TITLE;
				this.message = this.SUCCESS_MESSAGE;
				this.launchFullAlertModal({
					title: this.messageTitle,
					description: this.message,
					alertType: 'success',
					callbackName: 'Continue',
				});
				this.mobileService.clearChangePasswordCredential();
				this.submitting = false;
			})
			.catch((err) => {
				const restartProcess = err?.error?.code === '400213' || err?.error?.responseCode === '400213' || err?.error?.responseCode === LivelinessErrorCode.LIVELINESS_REFERENCE_CONSUMED;
				this.messageTitle = this.ERROR_TITLE;
				const callbackName = restartProcess ? 'Restart' : 'Close';
				this.message = StringHelper.getErrorMessageHttpErrorResponse(err);
				this.launchFullAlertModal({
					title: this.messageTitle,
					description: this.message,
					canShowLoading: restartProcess,
					callbackName,
				});
				this.submitting = false;
			});
	}

	launchFullAlertModal(params) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.alertType = params?.alertType || 'error';
		config.nzComponentParams.title = params?.title;
		config.className = DEFAULT_DIALOG_CLASSES;
		config.nzWidth = DEFAULT_FAILED_OTP_MODAL_WIDTH;
		config.nzComponentParams.canShowLoading = params?.canShowLoading || false;
		config.nzComponentParams.callback = () => {
			params?.canShowLoading ? this.restartProcess() : this.continue(params?.alertType);
		};
		config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
		config.nzComponentParams.expiryTime = params?.expiryTime;
		config.nzComponentParams.callbackName = params?.callbackName || 'Close';
		config.nzComponentParams.message = params?.description;
		this.runningModal = this.modalService.newalert(config);
	}

	setPassword($event: PasswordInput) {
		this.passwordInput = $event;
		this.isDisableButton = $event.validate();
	}

	goBack() {
		this.router.navigateByUrl(`/login`);
	}

	closeModal() {
		this.runningModal.destroy();
	}

	continue(alertType: string) {
		if (alertType === 'success') {
			this.mobileService.setChangePasswordCredential(null);
			this.goBack();
		} else {
			this.closeModal();
		}
	}
}
